import React, {useEffect, useState} from "react";
import {Route, Routes, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import "@komponentkassen/svv-form-css/dist/main.css";
import '@komponentkassen/svv-spinner-css/dist/main.css';
import {StepProp, SVVStepIndicator} from "@komponentkassen/svv-step-indicator";
import Section from "../shared/Section";
import DateAndTime from "../shared/DateAndTime";
import Confirmation from "../shared/Confirmation";
import ContactInfo from "../shared/ContactInfo";
import Receipt from "../shared/Receipt";
import trimStegviser from "../../helpers/TrimStegviser";
import InterfaceBooking from "../../interface/InterfaceBooking";
import InterfaceContactInfo from "../../interface/InterfaceContactInfo";
import InterfaceSection from "../../interface/InterfaceSection";
import InterfaceService from "../../interface/InterfaceService";
import EMPTY_BOOKING from "../../helpers/EmptyBooking";
import EMPTY_CONTACT_INFO from "../../helpers/EmptyContactInfo";
import api from "../../helpers/api";
import {EMPTY_SECTION} from "../../helpers/EmptySection";
import {useApptekster} from "../../apptekst-context";
import {ReminderString} from "../../helpers/SharedTypes";
import {BookingUrls, SessionStorageKeys, SessionStorageTypes} from "./types";
import {SVVSpinner} from "@komponentkassen/svv-spinner";
import {SVVLayoutBanner, SVVLayoutPageType, SVVLayoutPageWrapper} from "@komponentkassen/svv-layout";
import {TjenesteHeading} from "../shared/TjenesteHeading";
import {TjenesteTyper} from "../../types/tjenester";

const Book = ({tjeneste, avbrytUrl}: {tjeneste: string, avbrytUrl?: string}) => {
  const t = useApptekster();
  const [booking, setBooking] = useState<InterfaceBooking>(EMPTY_BOOKING);
  const [contactInfo, setContactInfo] = useState<InterfaceContactInfo>(EMPTY_CONTACT_INFO);
  const [section, setSection] = useState<InterfaceSection>(EMPTY_SECTION);
  //Defaulte til tung teori (første valget i lista)
  const [service, setService] = useState<InterfaceService>({
    serviceId: 438,
    serviceName: "Ta teoriprøve for",
    isTungKjoreToy: true
  });
  const [dateAndTime, setDateAndTime] = useState('');
  const [reserved, setReserved] = useState(false);
  const [navState, setNavState] = useState(0);
  const [sendReminderTo, setSendReminderTo] = useState<ReminderString>("both")
  const [gettingSessionStorageData, setGettingSessionStorageData] = useState(true)
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const routes: StepProp[] = [
    {
      href: BookingUrls.SECTION,
      name: t("timebestilling.velg-trafikkstasjon"),
      active: false,
    },
    {
      href: BookingUrls.DATE_AND_TIME,
      name: t("timebestilling.velg-tidspunkt"),
      active: false,
    },
    {
      href: BookingUrls.CONTACT,
      name: t("timebestilling.kontaktinformasjon"),
      active: false,
    },
    {
      href: BookingUrls.CONFIRMATION,
      name: t("timebestilling.oppsummering"),
      active: false,
    },
    {
      href: BookingUrls.RECEIPT,
      name: t("timebestilling.kvittering"),
      active: false,
    }
  ];

  const updateSessionStorage = ({key, value}: SessionStorageTypes)  => {
    sessionStorage.setItem(key, JSON.stringify(value))
  }

  const removeSessionStorage = (key: SessionStorageKeys) => {
    sessionStorage.removeItem(key)
  }

  useEffect(() => {
    if (sessionStorage.getItem(SessionStorageKeys.SERVICE)) {
      const serviceData: InterfaceService = JSON.parse(sessionStorage.getItem(SessionStorageKeys.SERVICE)!)
      setService(serviceData)
    }
    
    if (sessionStorage.getItem(SessionStorageKeys.SECTION)) {
      const sectionData: InterfaceSection = JSON.parse(sessionStorage.getItem(SessionStorageKeys.SECTION)!)
      setSection(sectionData)
    }
    
    const {pathname} = location
    let newNavState = 0
    switch (pathname) {
      case BookingUrls.SECTION:
        newNavState = 0
        break
      case BookingUrls.DATE_AND_TIME:
        newNavState = 1
        break
      case BookingUrls.CONTACT:
        newNavState = 2
        break
      case BookingUrls.CONFIRMATION:
        newNavState = 3
        break
      case BookingUrls.RECEIPT:
        newNavState = 4
        break
    }
    
    setNavState(newNavState)
    setGettingSessionStorageData(false)
    // Trenger ikke linte her, denne skal bare kjøre første gang komponenten rendres, ikke når location endres
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location) {
      let serviceData = sessionStorage.getItem(SessionStorageKeys.SERVICE)
      let sectionData = sessionStorage.getItem(SessionStorageKeys.SECTION)
      let navigateTo: BookingUrls.SERVICE | BookingUrls.SECTION | BookingUrls.DATE_AND_TIME | "" = ""

      if (location.pathname === BookingUrls.SECTION) {
        if (tjeneste) {
            let service: InterfaceService = {
              isTungKjoreToy: true,
              serviceId: 438,
              serviceName: "Teori",
              forklaring: t("timebestilling.tung-teori-forklaring")
            }

            switch (tjeneste) {
              case TjenesteTyper.teori:
                service.isTungKjoreToy = false
                service.serviceId = 438
                service.serviceName = "Teori"
                service.forklaring = t("timebestilling.lett-teori-forklaring")
                break;
              case TjenesteTyper.registrering:
                service.isTungKjoreToy = false
                service.serviceId = 437
                service.serviceName = "Registrering"
                service.forklaring = t("timebestilling.registrering-forklaring")
                break;
              case TjenesteTyper.forerkort:
                service.isTungKjoreToy = false
                service.serviceId = 436
                service.serviceName = "Førerkort"
                service.forklaring = t("timebestilling.forerkort-forklaring")
                break;
              case TjenesteTyper.fartsskriver:
                service.isTungKjoreToy = false
                service.serviceId = 436
                service.serviceName = "Førerkort"
                service.isFartsskriverProve = true
                service.forklaring = t("timebestilling.fartsskriver-forklaring")
                break
            }
            setService(service)
            updateSessionStorage({key: SessionStorageKeys.SERVICE, value: service})
        }
      } else {
        if (location.pathname === BookingUrls.SECTION && !serviceData) {
          removeSessionStorage(SessionStorageKeys.SECTION)
          navigate(BookingUrls.SERVICE);
        }
        if (location.pathname === BookingUrls.DATE_AND_TIME) {
          if (!serviceData && !sectionData) {
            navigateTo = BookingUrls.SERVICE
          }
          if (serviceData && !sectionData) {
            removeSessionStorage(SessionStorageKeys.SECTION)
            navigateTo = BookingUrls.SECTION
          }
          if (!serviceData) {
            removeSessionStorage(SessionStorageKeys.SECTION)
            removeSessionStorage(SessionStorageKeys.DATE_AND_TIME)
            navigateTo = BookingUrls.SERVICE
          }
        }
        if (navigateTo) {
          navigate(navigateTo);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  
  const resetBookingIdAndDateTime = () => {
    setReserved(false)
    setDateAndTime('')
    setBooking({...booking, bookingId: 0, bookedDateTime: ''})
    setNavState(routes.findIndex((r) => r.href === BookingUrls.DATE_AND_TIME))
  }
  const resetBookingSectionAndAndDateTime = () => {
    setReserved(false)
    setDateAndTime('')
    setSection(EMPTY_SECTION)
    setBooking({
      ...booking,
      bookingId: 0, 
      bookedDateTime: '', 
      sectionId: EMPTY_SECTION.sectionId, 
      sectionName: EMPTY_SECTION.sectionName
    })
    removeSessionStorage(SessionStorageKeys.SECTION)
    setNavState(routes.findIndex((r) => r.href === BookingUrls.SECTION))
  }
  
  function abort() {
    if (reserved && booking.bookingId) {
      api
        .get(
          `/api/Timebestilling/booking/unreserve?bookingId=${booking.bookingId}`
        )
        .then(function (response) {
          console.log('response fra unreserve: ', response.data);
        })
        .catch((error) => {
          // handle error
          console.log(error);
        })
        .finally(() => {
          setBooking(EMPTY_BOOKING);
          setReserved(false);
        });
    }
    if (avbrytUrl) {
      window.location.href = avbrytUrl;
    }
  }
  
  return (
    <>
      <SVVLayoutBanner 
        medium
        noPaddingTop
      >
        <TjenesteHeading 
          headingNokkel="timebestilling.handling.bestill"
        />
      </SVVLayoutBanner>
      <SVVLayoutPageWrapper>
        <SVVLayoutPageType medium>
          <div className="form-layout">
          {navState < 4 &&
          <div className="form-layout__nav">
            <SVVStepIndicator 
              routes={trimStegviser(routes, navState, window.location.pathname)} 
              ariaLabel={t("timebestilling.navigasjon")}
            />
          </div>
            }
            <div className={`form-layout__main ${gettingSessionStorageData ? 'form-layout--is-loading' : ''}`}>
              {gettingSessionStorageData &&
                <div className="form-layout__loader-container">
                  <SVVSpinner centered/>
                </div>
              }
              {!gettingSessionStorageData &&
                <Routes>
                  <Route
                    path="trafikkstasjon"
                    element={
                      <Section
                        section={section}
                        setSection={(s: InterfaceSection) => {
                          setSection(s)
                          updateSessionStorage({key: SessionStorageKeys.SECTION, value: s})
                        }}
                        setService={(s: InterfaceService) => {
                          setService(s)
                          updateSessionStorage({key: SessionStorageKeys.SERVICE, value: s})
                        }}
                        service={service}
                        setNavState={() => setNavState(navState + 1)}
                        abort={abort}
                        nextUrl={`/bestill/tidspunkt`}
                        resetBooking={resetBookingSectionAndAndDateTime}
                        avbrytUrl={avbrytUrl}
                      />
                    }
                  />
                  <Route
                    path="tidspunkt"
                    element={
                      <DateAndTime
                        sectionId={section.sectionId}
                        serviceId={service.serviceId}
                        isTungKjoreToy={service.isTungKjoreToy}
                        setDateAndTime={setDateAndTime}
                        dateAndTime={dateAndTime}
                        setNavState={() => setNavState(navState + 1)}
                        setBooking={setBooking}
                        setReserved={setReserved}
                        reserved={reserved}
                        bookingId={booking.bookingId}
                        resetBooking={resetBookingIdAndDateTime}
                        abort={abort}
                        nextUrl={`/bestill/kontakt?${searchParams}` }
                        prevUrl={`/bestill/trafikkstasjon?${searchParams}`}
                        avbrytUrl={avbrytUrl}
                      />
                    }
                  />
                  <Route
                    path="kontakt"
                    element={
                      <ContactInfo
                        originalBooking={booking}
                        setContactInfo={setContactInfo}
                        contactInfo={contactInfo}
                        setNavState={() => setNavState(3)}
                        abort={abort}
                        setSendReminderTo={setSendReminderTo}
                        backUrl="/bestill/tidspunkt"
                        avbrytUrl={avbrytUrl}
                      />
                    }
                  />
                  <Route
                    path="bekrefte"
                    element={
                      <Confirmation
                        originalBooking={booking}
                        setBooking={setBooking}
                        contactInfo={contactInfo}
                        dateAndTime={dateAndTime}
                        setNavState={() => setNavState(4)}
                        abort={abort}
                        confirmText={t("timebestilling.bestill")}
                        sendReminderTo={sendReminderTo}
                        avbrytUrl={avbrytUrl}
                      />
                    }
                  />
                  <Route
                    path="kvittering"
                    element={
                      <Receipt
                        isRebook={false}
                        booking={booking}
                        sendReminderTo={sendReminderTo}
                      />
                    }
                  />
                </Routes>  
              }
            </div>
          </div>
        </SVVLayoutPageType>
      </SVVLayoutPageWrapper>
    </>
  );
};

export default Book;
